import { onLogoutEvent } from '../auth/index';
import { buildLoginUrl } from '@thinkalpha/ui-core/loginUrl';
import { LOCATION_CHANGE, CALL_HISTORY_METHOD, push, replace } from 'redux-first-history';
import type { RouterActions } from 'redux-first-history';
import { all, call, put, select, takeEvery, getContext, takeLatest } from 'redux-saga/effects';
import { container } from 'src/StaticContainer';
import type { RootState } from 'src/store';
import { setPreviousLocation, setLastDashboardId } from 'src/store/actions/navigation';
import { setApplicationMenu } from 'src/store/actions/ui';
import { clearWorkspaceState } from 'src/store/actions/workspace';
import { ApplicationMenu } from 'src/store/types';
import type { SagaContext } from 'src/store/types';

const log = container.get('Logger').getSubLogger({ name: 'navigation sagas' });

function* updateNativeMenus() {
    const pathname: string = yield select((state: RootState) => state.router.location?.pathname ?? '');
    const loggedIn: boolean = yield select((state: RootState) => Boolean(state.auth.accessToken));
    const menus: RootState['ui']['menus'] = yield select((state: RootState) => state.ui.menus);

    if (!loggedIn) {
        if (pathname === '/login' && menus.application !== ApplicationMenu.loggedOutOpen) {
            yield put(setApplicationMenu(ApplicationMenu.loggedOutOpen));
        } else if (pathname === '/closed' && menus.application !== ApplicationMenu.loggedOutClosed) {
            yield put(setApplicationMenu(ApplicationMenu.loggedOutClosed));
        }
        return;
    } else if (menus.application !== ApplicationMenu.dashboard) {
        yield put(setApplicationMenu(ApplicationMenu.dashboard));
    }
}

function* performRedirects(action: RouterActions) {
    const bootstrapped: RootState['app']['bootstrapped'] = yield select((state: RootState) => state.app.bootstrapped);
    const location: RootState['router']['location'] = yield select((state: RootState) => state.router.location);
    const search = location?.search ?? '';
    const pathname = location?.pathname;

    if (
        // This bootstrap completion check seems to be unnecessary for web but is required on desktop right now
        !bootstrapped ||
        !pathname
    ) {
        return;
    }

    const accessToken: RootState['auth']['accessToken'] = yield select((state: RootState) => state.auth.accessToken);

    const publicRoutes = ['/login', '/register', '/closed', '/oauth2/callback', '/loggedout', '/account/open'];
    const publicPrefixes: string[] = [];

    if (
        !accessToken &&
        !publicRoutes.includes(pathname ?? '') &&
        !publicPrefixes.some((p) => (pathname ?? '').startsWith(p))
    ) {
        yield put(push(buildLoginUrl({ returnTo: pathname ?? undefined })));
    } else if (accessToken && pathname === '/') {
        if (TRACE) {
            log.trace({ message: 'Redirecting to /dashboard' });
        }
        yield put(replace('/dashboard'));
    } else if (!APP_ELECTRON && (pathname === '/login' || pathname === '/register')) {
        // On native, LoginController.start() handles OAuth initialization directly.
        if (TRACE) {
            log.trace({ message: 'starting oAuth in performRedirects', action: JSON.stringify(action) });
        }
        const returnTo = new URLSearchParams(search).get('returnTo') ?? '/dashboard';
        const extras: Record<string, string> | undefined = pathname === '/register' ? { prompt: 'create' } : undefined;
        const oAuth2Service: SagaContext['oAuth2Client'] = yield getContext('oAuth2Client');
        oAuth2Service.startOauth({ returnTo, extras });
    }
}

function* handleLogoutRoute(action: RouterActions) {
    if (action.type !== LOCATION_CHANGE) {
        return;
    }

    if (action.payload.location.pathname === '/logout') {
        if (TRACE) {
            log.trace({ message: 'handleLogoutRoute' });
        }
        yield call(onLogoutEvent);
    }
}

export function* handleNavigationTracking(action: RouterActions) {
    if (action.type !== LOCATION_CHANGE) {
        return;
    }

    const currentPath = action.payload.location.pathname;
    const previousPath: string | null = yield select((state: RootState) => state.navigation.previousLocation);

    if (TRACE) {
        log.trace({
            message: 'Navigation occurred',
            previousPath,
            currentPath,
        });
    }

    if (!currentPath.startsWith('/dashboard')) {
        const hasCurrentWorkspace: boolean = yield select(
            (state: RootState) => state.workspace.currentWorkspace !== null,
        );

        // On a hard-loaded dashboard, previousLocation can still be null on the first
        // navigation away. Clear any live workspace slice once the confirmed route is
        // off-dashboard so a denied save prompt cannot leak stale dirty state.
        if (previousPath?.startsWith('/dashboard') || hasCurrentWorkspace) {
            if (TRACE) {
                log.trace({ message: 'Clearing workspace state on navigation away from dashboard' });
            }
            yield put(clearWorkspaceState());
        }
    }

    // Update previous path for next navigation
    yield put(setPreviousLocation(currentPath));

    // If we're on a dashboard page, extract and store the dashboard ID
    if (currentPath?.startsWith('/dashboard')) {
        const pathParts = currentPath.split('/');
        const dashboardId = pathParts[2]; // /dashboard/{id}

        if (dashboardId) {
            yield put(setLastDashboardId(dashboardId));
            if (TRACE) {
                log.trace({ message: 'Stored last dashboard ID', dashboardId });
            }
        }
    }
}

export function* navigationSagas() {
    yield all([
        takeEvery(
            [LOCATION_CHANGE, CALL_HISTORY_METHOD, 'access-token::set', 'access-token::clear'],
            updateNativeMenus,
        ),
        takeEvery(['bootstrap::complete', LOCATION_CHANGE], performRedirects),
        takeLatest(LOCATION_CHANGE, handleLogoutRoute),
        takeLatest(LOCATION_CHANGE, handleNavigationTracking),
    ]);
}
